import React, { useEffect, useRef, useState } from 'react';
import './Home.css';
import GameTutorial from '../components/GameTutorial';

function useScrollReveal() {
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    const elements = ref.current?.querySelectorAll('.reveal-on-scroll');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return ref;
}

function Home() {
  const scrollRef = useScrollReveal();
  const videoRef = useRef(null);
  const phoneVideoRef = useRef(null);
  const [videoFaded, setVideoFaded] = useState(false);
  const [contentVisible, setContentVisible] = useState(false);
  const [phoneVideoEnded, setPhoneVideoEnded] = useState(false);

  useEffect(() => {
    const vid = videoRef.current;
    if (vid) {
      vid.playbackRate = 1.5;
    }
    const timer = setTimeout(() => {
      setVideoFaded(true);
      setTimeout(() => setContentVisible(true), 800);
    }, 3500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const phoneVid = phoneVideoRef.current;
    if (phoneVid && contentVisible) {
      // Small delay to ensure phone is visible before playing
      const playTimer = setTimeout(() => {
        phoneVid.currentTime = 0;
        phoneVid.play().catch(() => {});
      }, 500);
      const handleEnded = () => setPhoneVideoEnded(true);
      phoneVid.addEventListener('ended', handleEnded);
      return () => {
        clearTimeout(playTimer);
        phoneVid.removeEventListener('ended', handleEnded);
      };
    }
  }, [contentVisible]);

  return (
    <div className="home" ref={scrollRef}>
      {/* Hero Section */}
      <section className="hero">
        {/* Video Background */}
        <div className={`hero-video-wrap ${videoFaded ? 'faded' : ''}`}>
          <video
            ref={videoRef}
            className="hero-video"
            autoPlay
            muted
            playsInline
            loop
          >
            <source src="/videos/hero.mp4" type="video/mp4" />
          </video>
        </div>

        {/* Blue fade overlay — starts transparent, fades in when video ends */}
        <div className={`hero-fade-overlay ${videoFaded ? 'active' : ''}`}></div>

        {/* Background elements (only visible after fade) */}
        <div className={`hero-bg-elements ${videoFaded ? 'visible' : ''}`}>
          <div className="hero-glow hero-glow-1"></div>
          <div className="hero-glow hero-glow-2"></div>
          <div className="hero-glow hero-glow-3"></div>
        </div>

        {/* Content — fades in after video fades to blue */}
        <div className={`container hero-container ${contentVisible ? 'visible' : 'hidden-content'}`}>
          <div className="hero-content">
            <div className="hero-badge">NEW WORD GAME</div>
            <img src="/images/logo.png" alt="Wordalanche" className="hero-logo-img" />
            <p className="hero-tagline">Spell Fast. Score Big.</p>
            <p className="hero-subtitle">
              Letters fall. Words form. Scores soar. The addictive word puzzle game
              that will challenge your vocabulary and reflexes!
            </p>
            <div className="hero-cta">
              <a
                href="https://apps.apple.com/app/wordalanche"
                className="btn btn-primary"
                target="_blank"
                rel="noopener noreferrer"
              >
                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
                Download for iOS
              </a>
              <a href="#how-to-play" className="btn btn-secondary">
                Learn How to Play
              </a>
            </div>

          </div>

          <div className="hero-phone">
            <div className="phone-frame">
              <div className="phone-screen">
                <video
                  ref={phoneVideoRef}
                  className={`phone-video ${phoneVideoEnded ? 'ended' : ''}`}
                  muted
                  playsInline
                  preload="auto"
                >
                  <source src="/videos/phone-gameplay.mp4" type="video/mp4" />
                </video>
                <div className={`phone-resolve ${phoneVideoEnded ? 'visible' : ''}`}>
                  <img src="/images/logo.png" alt="Wordalanche" className="phone-resolve-logo" />
                  <p className="phone-resolve-tagline">Spell Fast. Score Big.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={`hero-scroll-indicator ${contentVisible ? 'visible' : 'hidden-content'}`}>
          <span>Scroll to explore</span>
          <div className="scroll-arrow"></div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="section stats-section">
        <div className="container">
          <div className="stats-grid">
            <div className="stat-item reveal-on-scroll">
              <span className="stat-number">178K+</span>
              <span className="stat-label">Valid Words</span>
            </div>
            <div className="stat-item reveal-on-scroll" style={{ transitionDelay: '0.1s' }}>
              <span className="stat-number">50+</span>
              <span className="stat-label">Achievements</span>
            </div>
            <div className="stat-item reveal-on-scroll" style={{ transitionDelay: '0.2s' }}>
              <span className="stat-number">4</span>
              <span className="stat-label">Power-Ups</span>
            </div>
            <div className="stat-item reveal-on-scroll" style={{ transitionDelay: '0.3s' }}>
              <span className="stat-number">8x</span>
              <span className="stat-label">Max Combo</span>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="section features-section">
        <div className="container">
          <div className="section-title reveal-on-scroll">
            <h2>Why You'll Love It</h2>
            <p>Wordalanche combines the best of word games with exciting arcade action</p>
          </div>

          <div className="features-grid">
            <div className="feature-card card reveal-on-scroll" style={{ transitionDelay: '0s' }}>
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
                </svg>
              </div>
              <h3>Fast-Paced Action</h3>
              <p>Letters fall onto a 6x8 grid, getting faster as you level up. Spell words before tiles hit the danger line!</p>
            </div>

            <div className="feature-card card reveal-on-scroll" style={{ transitionDelay: '0.1s' }}>
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                </svg>
              </div>
              <h3>Skillshot Bonuses</h3>
              <p>Match the target word length for a +50% bonus. Targets cycle through 3, 4, 5, 6, and 7 letters!</p>
            </div>

            <div className="feature-card card reveal-on-scroll" style={{ transitionDelay: '0.2s' }}>
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M8 14s1.5 2 4 2 4-2 4-2"/>
                  <line x1="9" y1="9" x2="9.01" y2="9"/>
                  <line x1="15" y1="9" x2="15.01" y2="9"/>
                </svg>
              </div>
              <h3>Special Tiles</h3>
              <p>Bombs, ice blocks, rainbow letters, star tiles, and 2x/3x multipliers - 6 tile types that change how you play!</p>
            </div>

            <div className="feature-card card reveal-on-scroll" style={{ transitionDelay: '0.3s' }}>
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/>
                  <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/>
                  <path d="M4 22h16"/>
                  <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/>
                  <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/>
                  <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/>
                </svg>
              </div>
              <h3>Achievements</h3>
              <p>Unlock 50+ achievements across 11 categories and earn gems to spend on powerful power-ups.</p>
            </div>

            <div className="feature-card card reveal-on-scroll" style={{ transitionDelay: '0.4s' }}>
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
                  <circle cx="12" cy="12" r="10" opacity="0.3"/>
                </svg>
              </div>
              <h3>8x Combo System</h3>
              <p>Submit words within 6 seconds of each other to build combos up to 8x. Speed is everything!</p>
            </div>

            <div className="feature-card card reveal-on-scroll" style={{ transitionDelay: '0.5s' }}>
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                  <path d="M2 17l10 5 10-5"/>
                  <path d="M2 12l10 5 10-5"/>
                </svg>
              </div>
              <h3>4 Power-Ups</h3>
              <p>Spend gems to clear rows, columns, refresh all letters, or freeze bomb timers when things get intense!</p>
            </div>
          </div>
        </div>
      </section>

      {/* How to Play Section */}
      <section id="how-to-play" className="section how-to-play-section">
        <div className="container">
          <div className="section-title reveal-on-scroll">
            <h2>How to Play</h2>
            <p>Master the avalanche with these core mechanics</p>
          </div>

          {/* Animated Tutorial Modules */}
          <GameTutorial />
        </div>
      </section>

      {/* Game Preview Section */}
      <section id="screenshots" className="section screenshots-section">
        <div className="container">
          <div className="section-title reveal-on-scroll">
            <h2>Built for Word Lovers</h2>
            <p>A game that's as beautiful as it is challenging</p>
          </div>

          <div className="game-highlights reveal-on-scroll">
            <div className="highlight-card">
              <div className="highlight-icon-wrap">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                  <path d="M2 17l10 5 10-5"/>
                  <path d="M2 12l10 5 10-5"/>
                </svg>
              </div>
              <h3>6x8 Grid</h3>
              <p>Strategic grid size means every letter placement matters</p>
            </div>
            <div className="highlight-card">
              <div className="highlight-icon-wrap">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12 6 12 12 16 14"/>
                </svg>
              </div>
              <h3>Real-Time Action</h3>
              <p>Tiles speed up as you level up - longer words score exponentially more points!</p>
            </div>
            <div className="highlight-card">
              <div className="highlight-icon-wrap">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                </svg>
              </div>
              <h3>Scrabble Dictionary</h3>
              <p>178,000+ valid words from the official Scrabble dictionary</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section cta-section">
        <div className="container">
          <div className="cta-content reveal-on-scroll">
            <h2>Ready to Start Your Avalanche?</h2>
            <p>Download now and see how high you can score!</p>
            <a
              href="https://apps.apple.com/app/wordalanche"
              className="btn btn-primary btn-large"
              target="_blank"
              rel="noopener noreferrer"
            >
              <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
              </svg>
              Download for iOS - It's Free!
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Home;
